import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  useTheme,
  alpha,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { AccessTime, ChevronLeft, ChevronRight, CalendarToday } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatTimeRange } from '../../utils/timeFormat';

// Generate 30-minute time slot windows from 00:00-00:30 to 23:30-24:00
// Starting from 12:00 AM (midnight) to include all 24 hours
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const startTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      let endHour = hour;
      let endMinute = minute + 30;
      if (endMinute >= 60) {
        endHour += 1;
        endMinute = 0;
      }
      const endTime = endHour < 24 
        ? `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`
        : '24:00';
      slots.push({
        start: startTime,
        end: endTime,
        key: startTime, // Use start time as key for compatibility (24-hour format for backend)
        display: formatTimeRange(startTime, endTime), // Display in 12-hour format
      });
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function DateAvailabilityCalendar({ value = [], onChange, disabled = false, monthsToShow = 1 }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateAvailability, setDateAvailability] = useState(() => {
    // Convert value array to object for easier access
    // Store as array of slot keys (start times) for compatibility
    const allSlotKeys = TIME_SLOTS.map(s => s.key);
    const availabilityMap = {};
    value.forEach(item => {
      // Convert time slots to array of keys (start times) if they're objects, or use as-is if strings
      const slots = item.timeSlots;
      // CRITICAL: Preserve empty arrays - they represent "all slots unavailable"
      if (Array.isArray(slots)) {
        if (slots.length === 0) {
          // Empty array means all slots unavailable - preserve it
          availabilityMap[item.date] = [];
        } else {
          // Filter to only include valid time slots (those that exist in TIME_SLOTS)
          const validSlots = slots
            .map(slot => typeof slot === 'string' ? slot : (slot.start || slot.key || slot))
            .filter(slotKey => allSlotKeys.includes(slotKey));
          if (validSlots.length > 0) {
            availabilityMap[item.date] = validSlots;
          }
        }
      } else if (!slots) {
        // If timeSlots is undefined/null, don't add to map (will default to all slots for new dates)
      }
    });
    return availabilityMap;
  });

  useEffect(() => {
    // Update local state when value prop changes
    // IMPORTANT: Preserve local state for dates that are currently selected/being edited
    // This prevents the useEffect from overwriting cleared dates while the dialog is open
    const allSlotKeys = TIME_SLOTS.map(s => s.key);
    const selectedKey = selectedDate ? formatDateKey(selectedDate) : null;
    
    setDateAvailability(prev => {
      const updated = { ...prev };
      
      // Update dates from value prop
      value.forEach(item => {
        const slots = item.timeSlots;
        // CRITICAL: Preserve empty arrays - they represent "all slots unavailable"
        if (Array.isArray(slots)) {
          if (slots.length === 0) {
            // Empty array means all slots unavailable - preserve it
            // Only update if it's not the selected date (preserve selected date's state)
            if (item.date !== selectedKey) {
              updated[item.date] = [];
            }
          } else {
            // Filter to only include valid time slots (those that exist in TIME_SLOTS)
            const validSlots = slots
              .map(slot => typeof slot === 'string' ? slot : (slot.start || slot.key || slot))
              .filter(slotKey => allSlotKeys.includes(slotKey));
            // Update if there are valid slots
            if (validSlots.length > 0) {
              // Only update if it's not the selected date, OR if it's selected and has slots
              if (item.date !== selectedKey || (item.date === selectedKey && validSlots.length > 0)) {
                updated[item.date] = validSlots;
              }
            }
          }
        }
      });
      
      // CRITICAL: Preserve dates that exist in prev but not in value prop
      // This handles the case where handleDateClick adds a new date and calls onChange,
      // but the useEffect runs before the parent's state updates with the new value prop
      // We need to preserve these newly added dates until the value prop catches up
      Object.keys(prev).forEach(dateKey => {
        // If date exists in prev but not in updated (not in value prop), preserve it
        // This ensures newly added dates aren't lost during the state sync
        if (!(dateKey in updated)) {
          updated[dateKey] = prev[dateKey];
        }
      });
      
      // CRITICAL: Preserve the selected date's state even if it's not in value (was cleared)
      // This ensures cleared dates remain cleared in the UI while dialog is open
      if (selectedKey && selectedKey in prev) {
        // Keep the selected date's state (could be empty array if cleared)
        updated[selectedKey] = prev[selectedKey];
      }
      
      return updated;
    });
  }, [value, selectedDate]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const formatDateKey = (date) => {
    // Use local date formatting to avoid timezone issues
    // toISOString() converts to UTC which can shift dates by one day
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // YYYY-MM-DD format
  };

  const handleDateClick = (date) => {
    if (!date) return;
    
    const dateKey = formatDateKey(date);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const clickedDate = new Date(date);
    clickedDate.setHours(0, 0, 0, 0);
    const isPast = clickedDate < today;
    
    if (isPast) {
      return;
    }
    
    // If disabled (read-only mode), just open the dialog to view time slots
    if (disabled) {
      // Always allow opening the dialog to view time slots (even if date has no specific availability)
      setSelectedDate(date);
      return;
    }
    
    // If date doesn't have availability yet, add it with empty slots by default (unavailable)
    // This ensures tutors must explicitly mark dates as available
    if (!(dateKey in dateAvailability)) {
      const updated = {
        ...dateAvailability,
        [dateKey]: [], // Default to unavailable (empty array)
      };
      setDateAvailability(updated);

      // IMPORTANT: Include dates with empty arrays so backend knows they were explicitly set
      // Strip any extra fields (like 'default') that might come from Mongoose
      const formatted = Object.entries(updated)
        .map(([date, timeSlots]) => ({
          date,
          timeSlots: Array.isArray(timeSlots) ? timeSlots : [],
        }));

      if (onChange) {
        onChange(formatted);
      }
    }
    
    setSelectedDate(date);
  };

  const handleTimeSlotToggle = (date, slotKey) => {
    if (disabled) return;

    const dateKey = formatDateKey(date);
    const allSlotKeys = TIME_SLOTS.map(slot => slot.key);
    // Get current slots for this date
    // If date exists in map, use its value (even if empty array)
    // If date doesn't exist, it's a new date, so default to empty array (unavailable)
    const currentSlots = dateKey in dateAvailability
      ? dateAvailability[dateKey]
      : [];
    const isSelected = currentSlots.includes(slotKey);
    
    const newSlots = isSelected
      ? currentSlots.filter(slot => slot !== slotKey)
      : [...currentSlots, slotKey].sort();

    const updated = {
      ...dateAvailability,
      [dateKey]: newSlots,
    };

    setDateAvailability(updated);

    // Convert to format expected by parent
    // IMPORTANT: Include dates with empty arrays so backend knows they were cleared
    // Strip any extra fields (like 'default') that might come from Mongoose
    const formatted = Object.entries(updated)
      .map(([date, timeSlots]) => ({
        date,
        timeSlots: Array.isArray(timeSlots) ? timeSlots : [],
      }));

    if (onChange) {
      onChange(formatted);
    }
  };

  const handleSelectAllTimeSlots = (date) => {
    if (disabled) return;

    const dateKey = formatDateKey(date);
    const allSlotKeys = TIME_SLOTS.map(slot => slot.key);
    const updated = {
      ...dateAvailability,
      [dateKey]: [...allSlotKeys],
    };

    setDateAvailability(updated);

    // IMPORTANT: Include dates with empty arrays so backend knows they were cleared
    // Strip any extra fields (like 'default') that might come from Mongoose
    const formatted = Object.entries(updated)
      .map(([date, timeSlots]) => ({
        date,
        timeSlots: Array.isArray(timeSlots) ? timeSlots : [],
      }));

    if (onChange) {
      onChange(formatted);
    }
  };

  const handleClearTimeSlots = (date) => {
    if (disabled) return;

    const dateKey = formatDateKey(date);
    
    const updated = {
      ...dateAvailability,
      [dateKey]: [], // Clear all slots but keep the date entry
    };

    setDateAvailability(updated);

    // IMPORTANT: Include dates with empty arrays so backend knows they were cleared
    // This allows the backend to save the cleared state
    const formatted = Object.entries(updated)
      .map(([date, timeSlots]) => ({
        date,
        timeSlots,
      }));

    if (onChange) {
      onChange(formatted);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - monthsToShow, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + monthsToShow, 1));
  };

  const displayedMonths = Array.from({ length: monthsToShow }, (_, offset) =>
    new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1)
  );

  const selectedDateKey = selectedDate ? formatDateKey(selectedDate) : null;
  const allSlotKeys = TIME_SLOTS.map(slot => slot.key);
  // Check if date key exists in availability map
  // If it exists, use its value (even if empty array - this represents cleared/unavailable state)
  // If it doesn't exist, default to empty array (new date that hasn't been configured - unavailable by default)
  // IMPORTANT: Empty array [] means the date is unavailable (either explicitly cleared or not configured)
  const selectedDateSlots = selectedDateKey 
    ? (selectedDateKey in dateAvailability 
        ? dateAvailability[selectedDateKey]  // This could be [] (unavailable) or array of slots (available)
        : [])  // New date, default to unavailable (empty array)
    : [];

  // Group time slots by AM/PM for better display
  const groupedByPeriod = TIME_SLOTS.reduce((acc, slot) => {
    const hour = parseInt(slot.start.split(':')[0]);
    const period = hour < 12 ? 'AM' : 'PM';
    if (!acc[period]) {
      acc[period] = [];
    }
    acc[period].push(slot);
    return acc;
  }, {});
  
  // Sort slots within each period by hour
  Object.keys(groupedByPeriod).forEach(period => {
    groupedByPeriod[period].sort((a, b) => {
      const hourA = parseInt(a.start.split(':')[0]);
      const hourB = parseInt(b.start.split(':')[0]);
      return hourA - hourB;
    });
  });

  const getVisibleRangeLabel = () => {
    const firstMonth = displayedMonths[0];
    const lastMonth = displayedMonths[displayedMonths.length - 1];
    const firstLabel = `${MONTHS[firstMonth.getMonth()]} ${firstMonth.getFullYear()}`;
    if (displayedMonths.length === 1) {
      return firstLabel;
    }
    const lastLabel = `${MONTHS[lastMonth.getMonth()]} ${lastMonth.getFullYear()}`;
    return `${firstLabel} - ${lastLabel}`;
  };

  const renderMonthGrid = (monthDate, monthIndex) => {
    const days = getDaysInMonth(monthDate);
    const month = monthDate.getMonth();
    const year = monthDate.getFullYear();

    return (
      <Box
        key={`${year}-${month}`}
        sx={{
          border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'white',
        }}
      >
        <Box
          sx={{
            p: 1.5,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            {MONTHS[month]} {year}
          </Typography>
        </Box>

        <Grid container>
          {DAYS_OF_WEEK.map((day) => (
            <Grid
              item
              xs={12 / 7}
              key={`${monthIndex}-${day}`}
              sx={{
                p: 1,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:last-child': {
                  borderRight: 'none',
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Grid container>
          {days.map((date, index) => {
            if (!date) {
              return (
                <Grid
                  item
                  xs={12 / 7}
                  key={`empty-${monthIndex}-${index}`}
                  sx={{
                    aspectRatio: '1',
                    borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                />
              );
            }

            const dateKey = formatDateKey(date);
            const isSelected = selectedDateKey === dateKey;
            const hasAvailability = dateKey in dateAvailability;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const cellDate = new Date(date);
            cellDate.setHours(0, 0, 0, 0);
            const isToday = formatDateKey(today) === dateKey;
            const isPast = cellDate < today;

            let dateColor = 'transparent';
            let borderColor = 'transparent';
            let showNoInfo = false;
            if (!isPast) {
              if (!hasAvailability) {
                dateColor = alpha('#9e9e9e', 0.1);
                borderColor = alpha('#9e9e9e', 0.3);
                showNoInfo = true;
              } else {
                const slots = dateAvailability[dateKey] || [];
                const totalSlots = TIME_SLOTS.length;
                if (slots.length === 0) {
                  dateColor = alpha(theme.palette.error?.main || '#f44336', 0.15);
                  borderColor = theme.palette.error?.main || '#f44336';
                } else if (slots.length < totalSlots) {
                  dateColor = alpha('#ffeb3b', 0.15);
                  borderColor = '#ffeb3b';
                } else {
                  dateColor = alpha(theme.palette.success?.main || '#4caf50', 0.15);
                  borderColor = theme.palette.success?.main || '#4caf50';
                }
              }
            }

            return (
              <Grid
                item
                xs={12 / 7}
                key={`${monthIndex}-${dateKey}`}
                sx={{
                  aspectRatio: '1',
                  borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  '&:nth-of-type(7n)': {
                    borderRight: 'none',
                  },
                }}
              >
                <Box
                  onClick={() => {
                    handleDateClick(date);
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isPast ? 'default' : 'pointer',
                    bgcolor: isSelected
                      ? alpha(theme.palette.primary.main, 0.2)
                      : dateColor,
                    border: isSelected
                      ? `2px solid ${theme.palette.primary.main}`
                      : isToday
                      ? `2px solid ${theme.palette.secondary.main}`
                      : !isPast && borderColor !== 'transparent'
                      ? `2px solid ${borderColor}`
                      : '2px solid transparent',
                    transition: 'all 0.2s ease',
                    opacity: isPast ? 0.5 : 1,
                    '&:hover': isPast
                      ? {}
                      : {
                          bgcolor: isSelected
                            ? alpha(theme.palette.primary.main, 0.25)
                            : borderColor !== 'transparent'
                              ? alpha(borderColor || theme.palette.primary.main, 0.2)
                              : alpha(theme.palette.primary.main, 0.1),
                        },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isToday ? 700 : hasAvailability ? 600 : 400,
                      color: isSelected
                        ? theme.palette.primary.main
                        : isToday
                        ? theme.palette.secondary.main
                        : showNoInfo
                        ? '#9e9e9e'
                        : 'text.primary',
                    }}
                  >
                    {date.getDate()}
                  </Typography>
                  {showNoInfo && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.6rem',
                        color: '#9e9e9e',
                        mt: 0.25,
                        textAlign: 'center',
                        lineHeight: 1,
                      }}
                    >
                      {t('tutorProfile.noInfo')}
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      {/* Calendar Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <IconButton
          onClick={handlePreviousMonth}
          sx={{ color: theme.palette.primary.main }}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
          {getVisibleRangeLabel()}
        </Typography>
        <IconButton
          onClick={handleNextMonth}
          sx={{ color: theme.palette.primary.main }}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Calendar Grids */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: monthsToShow > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
          },
          gap: 2,
        }}
      >
        {displayedMonths.map((monthDate, index) => renderMonthGrid(monthDate, index))}
      </Box>

      {/* Time Slots Dialog for Selected Date */}
      <Dialog
        open={selectedDate !== null}
        onClose={() => setSelectedDate(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {selectedDate &&
                  selectedDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => selectedDate && handleSelectAllTimeSlots(selectedDate)}
                disabled={disabled || selectedDateSlots.length === TIME_SLOTS.length}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                }}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => {
                  if (selectedDate) {
                    handleClearTimeSlots(selectedDate);
                  }
                }}
                disabled={disabled || !selectedDateKey || !(selectedDateKey in dateAvailability) || (selectedDateKey in dateAvailability && dateAvailability[selectedDateKey].length === 0)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                }}
              >
                Clear All
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {disabled 
                ? (selectedDateKey && selectedDateKey in dateAvailability && dateAvailability[selectedDateKey].length > 0
                    ? t('tutorProfile.timeSlotsAvailable')
                    : selectedDateKey && selectedDateKey in dateAvailability && dateAvailability[selectedDateKey].length === 0
                    ? t('tutorProfile.dateMarkedUnavailable')
                    : t('tutorProfile.noAvailabilityInfo'))
                : t('tutorProfile.selectTimeSlotsInstruction')}
            </Typography>
            <Box
              sx={{
                maxHeight: '500px',
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: alpha(theme.palette.primary.main, 0.05),
                },
                '&::-webkit-scrollbar-thumb': {
                  background: alpha(theme.palette.primary.main, 0.3),
                  borderRadius: '3px',
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.5),
                  },
                },
              }}
            >
              <Grid container spacing={2}>
                {['AM', 'PM'].map((period) => {
                  const periodSlots = groupedByPeriod[period] || [];
                  if (periodSlots.length === 0) return null;
                  
                  return (
                    <Grid item xs={12} md={6} key={period}>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            mb: 1.5,
                            fontSize: '0.9rem',
                          }}
                        >
                          {period}
                        </Typography>
                        <Stack spacing={0.75}>
                          {periodSlots.map((slot) => {
                            const isSelected = selectedDateSlots.includes(slot.key);
                            return (
                              <Chip
                                key={slot.key}
                                label={slot.display}
                                onClick={() => selectedDate && handleTimeSlotToggle(selectedDate, slot.key)}
                                disabled={disabled}
                                size="small"
                                sx={{
                                  bgcolor: isSelected
                                    ? theme.palette.success?.main || '#4caf50'
                                    : theme.palette.error?.main || '#f44336',
                                  color: 'white',
                                  border: `1px solid ${
                                    isSelected
                                      ? theme.palette.success?.main || '#4caf50'
                                      : theme.palette.error?.main || '#f44336'
                                  }`,
                                  fontWeight: 600,
                                  cursor: disabled ? 'default' : 'pointer',
                                  fontSize: '0.7rem',
                                  height: '28px',
                                  width: '100%',
                                  justifyContent: 'flex-start',
                                  opacity: disabled ? 1 : 1, // Keep full opacity even when disabled (read-only)
                                  '&.Mui-disabled': {
                                    opacity: 1, // Override MUI's default disabled opacity
                                    bgcolor: isSelected
                                      ? theme.palette.success?.main || '#4caf50'
                                      : theme.palette.error?.main || '#f44336',
                                    color: 'white',
                                    border: `1px solid ${
                                      isSelected
                                        ? theme.palette.success?.main || '#4caf50'
                                        : theme.palette.error?.main || '#f44336'
                                    }`,
                                  },
                                  '&:hover': disabled
                                    ? {}
                                    : {
                                        bgcolor: isSelected
                                          ? theme.palette.success?.dark || '#388e3c'
                                          : theme.palette.error?.dark || '#d32f2f',
                                      },
                                }}
                              />
                            );
                          })}
                        </Stack>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDate(null)} sx={{ borderRadius: 2 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DateAvailabilityCalendar;

